import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useCustomerAuth } from "../components/CustomerLayout";
import { 
  Heart, 
  ShoppingBag, 
  Trash2, 
  Bell,
  BellOff,
  Loader2,
  Star,
  AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const getImageSrc = (product) => {
  const image = product?.image || product?.photo;
  if (!image) return null;
  if (image.startsWith('data:')) return image;
  if (image.startsWith('http')) return image;
  return `data:image/jpeg;base64,${image}`;
};

export default function CustomerFavorites() {
  const { userId } = useParams();
  const { getAuthHeaders, refreshCounts, devise } = useCustomerAuth();
  
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/api/customer/favorites`, getAuthHeaders());
      setFavorites(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des favoris");
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (productId) => {
    try {
      await axios.delete(`${API}/api/customer/favorites/${productId}`, getAuthHeaders());
      setFavorites(favorites.filter(f => f.product_id !== productId));
      refreshCounts();
      toast.success("Retiré des favoris");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleAlert = async (productId, currentAlert) => {
    try {
      await axios.put(
        `${API}/api/customer/favorites/${productId}/alert?alert=${!currentAlert}`,
        {},
        getAuthHeaders()
      );
      setFavorites(favorites.map(f => 
        f.product_id === productId 
          ? { ...f, alert_when_available: !currentAlert }
          : f
      ));
      toast.success(!currentAlert ? "Alerte activée" : "Alerte désactivée");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const updatePreferredSize = async (productId, size) => {
    try {
      await axios.post(
        `${API}/api/customer/favorites`,
        { product_id: productId, preferred_size: size },
        getAuthHeaders()
      );
      setFavorites(favorites.map(f => 
        f.product_id === productId 
          ? { ...f, preferred_size: size }
          : f
      ));
      toast.success("Taille préférée enregistrée");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Heart className="w-10 h-10 text-[#D4AF37]" />
        </div>
        <h2 className="text-xl font-semibold text-[#1B1B1B] mb-2">
          Vous n'avez pas encore de favoris
        </h2>
        <p className="text-[#888888] mb-6">
          Parcourez la boutique et sauvegardez vos articles préférés !
        </p>
        <Link to={`/vitrine/${userId}`}>
          <Button className="bg-[#D4AF37] hover:bg-[#B8963C] text-white rounded-xl">
            Voir la boutique →
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      <h2 className="text-lg font-semibold text-[#1B1B1B] mb-4">
        Mes favoris ({favorites.length})
      </h2>

      <div className="grid grid-cols-2 gap-4">
        {favorites.map((fav) => {
          const product = fav.product;
          if (!product) return null;
          
          return (
            <div
              key={fav.id}
              className="bg-white rounded-xl overflow-hidden shadow-sm border border-[#F0F0F0]"
            >
              {/* Product Image */}
              <div className="aspect-square relative bg-gradient-to-br from-[#F5F5F5] to-[#EBEBEB] overflow-hidden">
                {getImageSrc(product) ? (
                  <img
                    src={getImageSrc(product)}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg viewBox="0 0 100 100" className="w-16 h-16 text-[#D5D5D5]">
                      <ellipse cx="50" cy="25" rx="12" ry="12" fill="currentColor"/>
                      <path d="M30 95 L35 45 Q50 35 65 45 L70 95 Z" fill="currentColor"/>
                    </svg>
                  </div>
                )}
                
                {/* Out of stock overlay */}
                {fav.is_out_of_stock && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Badge variant="secondary" className="bg-gray-600 text-white">
                      Indisponible
                    </Badge>
                  </div>
                )}
                
                {/* Price drop badge */}
                {fav.price_drop && fav.price_drop > 0 && (
                  <Badge className="absolute top-2 left-2 bg-green-500 text-white">
                    Prix baissé ! -{fav.price_drop.toLocaleString('fr-FR')} {devise || '€'}
                  </Badge>
                )}

                {/* Remove button */}
                <button
                  onClick={() => removeFavorite(fav.product_id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Product Info */}
              <div className="p-3 space-y-2">
                <h3 className="font-medium text-[#1B1B1B] text-sm line-clamp-1">
                  {product.name}
                </h3>
                
                <p className="text-[#D4AF37] font-bold">
                  {product.sale_price?.toLocaleString('fr-FR')} {devise || '€'}
                </p>

                {/* Preferred Size Selector */}
                <Select
                  value={fav.preferred_size || ""}
                  onValueChange={(value) => updatePreferredSize(fav.product_id, value)}
                >
                  <SelectTrigger className="h-9 text-xs bg-[#F5F5F5] border-0 rounded-lg">
                    <SelectValue placeholder="Ma taille" />
                  </SelectTrigger>
                  <SelectContent>
                    {product.available_sizes?.map((size) => (
                      <SelectItem key={size} value={size}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Actions */}
                <div className="flex gap-2">
                  {fav.is_out_of_stock ? (
                    <Button
                      variant={fav.alert_when_available ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleAlert(fav.product_id, fav.alert_when_available)}
                      className={`flex-1 text-xs rounded-lg ${
                        fav.alert_when_available 
                          ? "bg-[#D4AF37] hover:bg-[#B8963C] text-white" 
                          : "border-[#D4AF37] text-[#D4AF37]"
                      }`}
                    >
                      {fav.alert_when_available ? (
                        <>
                          <Bell className="w-3 h-3 mr-1" />
                          Alerte activée
                        </>
                      ) : (
                        <>
                          <BellOff className="w-3 h-3 mr-1" />
                          M'alerter
                        </>
                      )}
                    </Button>
                  ) : (
                    <Link to={`/vitrine/${userId}?product=${fav.product_id}`} className="flex-1">
                      <Button 
                        size="sm"
                        className="w-full text-xs rounded-lg bg-[#D4AF37] hover:bg-[#B8963C] text-white"
                      >
                        <ShoppingBag className="w-3 h-3 mr-1" />
                        Commander
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
